import type { UserPrivate } from '@repo/types/users';

import { useDeleteUser } from '@repo/api/user/useDeleteUser';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { BookTextIcon } from 'lucide-react';
import { useEffect, useMemo, type ReactNode } from 'react';

import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

/**
 * Props for the DeleteUser component.
 * @property {UserPrivate[] | null} [users] - Array of users to delete.
 * @property {boolean} open - Whether the dialog is open.
 * @property {(value: boolean) => void} onOpenChange - Callback to change dialog open state.
 */
interface DeleteUserDialogProps {
  users?: UserPrivate[] | null;
  open: boolean;
  onOpenChange: (value: boolean) => void;
}

export default function DeleteUserDialog(deleteUserDialogProps: DeleteUserDialogProps) {
  const { users, open, onOpenChange } = deleteUserDialogProps;
  const isSingle: boolean = users?.length === 1;

  const { mutateAsync, reset } = useDeleteUser();

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, reset]);

  const onSubmit = async () => {
    if (!users) return;
    await Promise.all(users.map((element) => mutateAsync(element.id))).catch((error) => {
      toasterMessageHandler(baseErrorMessages, error);
    });
    onClose();
  };

  const onClose = () => {
    onOpenChange(false);
  };

  const header = useMemo<string>(() => {
    if (isSingle) {
      return ' Usunąć użytkownika ?';
    } else {
      return `Usunąć ${users?.length} użytkowników?`;
    }
  }, [isSingle, users]);

  const content = useMemo<ReactNode>(() => {
    if (isSingle) {
      return (
        <p className={'text-text-secondary'}>
          Czy na pewno chcesz usunąć użytkownika{'  '}
          <b>{users?.[0]?.name}</b>?
        </p>
      );
    } else {
      return <p className={'text-text-secondary'}>Czy na pewno chcesz usunąć wybranych użytkowników ?</p>;
    }
  }, [isSingle, users]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className={'sm:max-w-96'}>
        <AlertDialogBody>
          <AlertDialogHeader>
            <FeaturedIcon>
              <BookTextIcon />
            </FeaturedIcon>
            <div className={'flex min-w-0 grow flex-col gap-0.5'}>
              <AlertDialogTitle>{header}</AlertDialogTitle>
              <AlertDialogDescription className={'text-muted-foreground text-xs'}>Usuń wybranych użytkowników/a.</AlertDialogDescription>
            </div>
          </AlertDialogHeader>
          <AlertDialogDescription className={'sr-only'}>Usuń wybranych użytkowników</AlertDialogDescription>
          <div className={'flex flex-col gap-3 overflow-auto p-4'}>
            {content}
            {!isSingle && users && (
              <div className={'flex flex-col gap-3'}>
                <h5 className={'text-muted-foreground text-sm font-medium'}>Użytkownicy:</h5>
                <div className={'flex flex-wrap gap-2'}>
                  {users.map(({ id, name }) => {
                    return (
                      <Badge key={id} size={'sm'} variant={'muted'}>
                        {name}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </AlertDialogBody>
        <AlertDialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
          <AlertDialogCancel variant={'tertiary'} onClick={onClose}>
            Zamknij
          </AlertDialogCancel>
          <AlertDialogAction onClick={onSubmit}>Potwierdź</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
